import type { BreadcrumbItemType } from "@shared/ui"
import { ROUTE_LABELS, ROUTE_HIERARCHY } from "@/consts"

const formatDashedString = (str: string): string => {
  const words = str.split('-')
  if (!words || words.length === 0) return str

  const capitalizedWords = words.map(word => {
    if (!word) return ''
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  })

  return capitalizedWords.join(' ')
}

const getRouteLabel = (segment: string): string => {
  return ROUTE_LABELS[segment] ?? (
    segment.includes('-')
      ? formatDashedString(segment)
      : segment.charAt(0).toUpperCase() + segment.slice(1)
  )
}

const getNestedRoutes = (pathname: string): { href: string; label: string }[] => {
  const segments = pathname.split("/").filter(Boolean)
  
  if (segments.length >= 3) {
    let currentLevel: Record<string, any> = ROUTE_HIERARCHY
    
    for (let i = 0; i < 2 && i < segments.length; i++) {
      const path = "/" + segments.slice(0, i + 1).join("/")
      if (currentLevel[path] && typeof currentLevel[path] === 'object') {
        currentLevel = currentLevel[path] as Record<string, unknown>
      } else {
        return []
      }
    }
    
    return Object.keys(currentLevel).map(href => ({
      href,
      label: getRouteLabel(href.split('/').pop() || '')
    }))
  }
  
  return []
}

export function generateBreadcrumbItems(
  pathname: string,
  config: Record<string, string> = {}
): BreadcrumbItemType[] {
  const segments = pathname.split("/").filter(Boolean)
  
  if (segments.length <= 2) {
    return segments.map((segment, index) => {
      const href = "/" + segments.slice(0, index + 1).join("/")
      const label = config[segment] ?? getRouteLabel(segment)

      return {
        type: index === segments.length - 1 ? "page" : "link",
        href,
        label,
      }
    })
  }
  
  const result: BreadcrumbItemType[] = []
  
  const firstSegment = segments[0]
  if (firstSegment) {
    const firstHref = "/" + firstSegment
    result.push({
      type: "link",
      href: firstHref,
      label: config[firstSegment] ?? getRouteLabel(firstSegment),
    })
  }
  
  const nestedRoutes = getNestedRoutes(pathname)
  if (nestedRoutes.length > 0) {
    result.push({
      type: "dropdown",
      label: "More",
      menuItems: nestedRoutes,
    })
  }
  
  const lastSegment = segments[segments.length - 1]
  if (lastSegment) {
    result.push({
      type: "page",
      label: config[lastSegment] ?? getRouteLabel(lastSegment),
    })
  }
  
  return result
}
